import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { errorEvent } from '~errors/emitter';
import { useCurrentLanguage } from '~hooks/language';
import { useIntelliProveAPI } from '~providers/api';
import { Theming, UrlSettings } from '~schemas/settings';
import { FeatureFlags, SettingsResponse } from '~services/api/schemas/responses/settings_response';
import { buildThemingFromBranding } from '~utils/theming';

interface iSkipPageSettings {
  welcome: boolean;
  privacy: boolean;
}

interface iSettingsContext {
  urls?: UrlSettings;
  theming?: Theming;
  features?: FeatureFlags;
  skip?: iSkipPageSettings;
}

const SettingsContext = createContext<iSettingsContext>({});

interface iSettingsProvider {
  children: React.ReactNode | React.ReactNode[];
}

export function SettingsProvider({ children }: iSettingsProvider) {
  const api = useIntelliProveAPI();
  const [settings, setSettings] = useState<SettingsResponse>(null!);
  const [theming, setTheming] = useState<Theming | undefined>(undefined);
  const currentLanguage = useCurrentLanguage();

  useEffect(() => {
    api
      .getWippySettings()
      .then((resp) => {
        setSettings(resp);
      })
      .catch((e) => {
        errorEvent.emit({
          error: e,
          message: 'Failed to fetch wippy settings',
        });
      });
  }, [api]);

  useEffect(() => {
    api
      .getBranding()
      .then((branding) => {
        setTheming(buildThemingFromBranding(branding));
      })
      .catch((e) => {
        errorEvent.emit({
          error: e,
          message: 'Failed to fetch branding',
        });
      });
  }, [api]);

  const urls = useMemo<UrlSettings | undefined>(() => {
    if (!settings) return;
    if (!(currentLanguage in settings.urls)) return;
    return settings.urls[currentLanguage as 'en' | 'nl' | 'fr' | 'da'];
  }, [currentLanguage, settings]);

  return (
    <SettingsContext.Provider
      value={{
        urls: urls,
        theming: theming,
        features: settings?.features,
        skip: {
          welcome: settings?.skip_welcome,
          privacy: settings?.skip_privacy,
        },
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = (): iSettingsContext => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within an SettingsProvider');
  }
  return context;
};
